import * as THREE from 'three';
import {
  createGlowMaterial,
  createHaloMaterial,
  createMatteMaterial,
  palette
} from '../../visual';
import {
  CEILING_HEIGHT,
  EXIT_PAD_Z,
  EXIT_PAD_RADIUS,
  FLOOR_Y,
  HANGAR_CENTER_Z,
  INTERIOR_RADIUS,
  NPC_DOCK_OFFSET,
  PLAYER_SHIP_DOCK_Z,
  HANGAR_PLATFORM_RAISE
} from './StationLayout';

export interface HangarBuildResult {
  readonly root: THREE.Group;
  readonly exitPosition: THREE.Vector3;
  readonly exitRadius: number;
  readonly playerShipPlatformPosition: THREE.Vector3;
  readonly npcDockPositions: THREE.Vector3[];
  readonly obstacles: ReadonlyArray<{
    readonly position: THREE.Vector3;
    readonly radius: number;
  }>;
}

/**
 * Builds the hangar zone on the +Z side of the station: a raised platform
 * with a panoramic window arch looking out into space, beacon columns
 * flanking the exit pad, and docks for the player's ship plus two NPC ships.
 */
export function buildHangar(): HangarBuildResult {
  const root = new THREE.Group();
  root.name = 'station-hangar';
  const obstacles: { position: THREE.Vector3; radius: number }[] = [];

  const darkMat = createMatteMaterial({ color: 0x141828, roughness: 0.75 });
  const hullMat = createMatteMaterial({
    color: palette.station.hull,
    roughness: 0.5
  });
  const trimMat = createMatteMaterial({
    color: palette.station.hullMid,
    roughness: 0.55
  });
  const warmAccent = createMatteMaterial({
    color: 0xffb568,
    roughness: 0.35,
    emissive: 0xffb568,
    emissiveIntensity: 0.38
  });
  const warmGlow = createGlowMaterial(0xffd389, 0.95, true);
  const windowGlow = createGlowMaterial(0x0b1230, 0.95, true);

  // ----- HANGAR FLOOR PLATFORM (raised) ------------------------------
  const platformWidth = INTERIOR_RADIUS * 0.8;
  const platformDepth = INTERIOR_RADIUS * 0.5;

  const hangarFloor = new THREE.Mesh(
    new THREE.BoxGeometry(platformWidth, 0.5, platformDepth),
    darkMat
  );
  hangarFloor.position.set(
    0,
    HANGAR_PLATFORM_RAISE + FLOOR_Y,
    HANGAR_CENTER_Z
  );
  root.add(hangarFloor);

  // Edge strip around the platform lip.
  const lipGeo = new THREE.BoxGeometry(platformWidth + 0.2, 0.08, 0.15);
  const lipFront = new THREE.Mesh(lipGeo, warmAccent);
  lipFront.position.set(
    0,
    HANGAR_PLATFORM_RAISE + 0.25 + FLOOR_Y,
    HANGAR_CENTER_Z - platformDepth / 2
  );
  root.add(lipFront);

  // Steps from atrium onto the platform (the -Z edge).
  for (let step = 0; step < 2; step++) {
    const stepMesh = new THREE.Mesh(
      new THREE.BoxGeometry(platformWidth + 0.6 - step * 0.5, 0.22, 0.55),
      trimMat
    );
    stepMesh.position.set(
      0,
      0.11 + step * 0.22 + FLOOR_Y,
      HANGAR_CENTER_Z - platformDepth / 2 - 0.55 - step * 0.55
    );
    root.add(stepMesh);
  }

  // ----- VIEW WINDOW ARCH (huge arched window on the +Z wall) --------
  // Outer frame arch (box with a hole cut out via two blocks stacked).
  const archFrameMat = createMatteMaterial({
    color: 0x1a2134,
    roughness: 0.7
  });
  const frameWidth = platformWidth + 4;
  const frameHeight = CEILING_HEIGHT * 0.75;

  const leftPost = new THREE.Mesh(
    new THREE.BoxGeometry(1.4, frameHeight, 1.4),
    archFrameMat
  );
  leftPost.position.set(
    -frameWidth / 2 + 0.7,
    frameHeight / 2 + FLOOR_Y,
    HANGAR_CENTER_Z + platformDepth / 2 + 0.2
  );
  root.add(leftPost);
  obstacles.push({
    position: new THREE.Vector3(
      leftPost.position.x,
      0,
      leftPost.position.z
    ),
    radius: 0.9
  });

  const rightPost = leftPost.clone();
  rightPost.position.x = frameWidth / 2 - 0.7;
  root.add(rightPost);
  obstacles.push({
    position: new THREE.Vector3(
      rightPost.position.x,
      0,
      rightPost.position.z
    ),
    radius: 0.9
  });

  // Top lintel.
  const lintel = new THREE.Mesh(
    new THREE.BoxGeometry(frameWidth, 1.5, 1.4),
    archFrameMat
  );
  lintel.position.set(
    0,
    frameHeight + 0.75 + FLOOR_Y,
    HANGAR_CENTER_Z + platformDepth / 2 + 0.2
  );
  root.add(lintel);

  // Accent glow running along the lintel.
  const lintelGlow = new THREE.Mesh(
    new THREE.BoxGeometry(frameWidth - 1.5, 0.18, 1.5),
    warmAccent
  );
  lintelGlow.position.set(
    0,
    frameHeight + 0.08 + FLOOR_Y,
    HANGAR_CENTER_Z + platformDepth / 2 + 0.2
  );
  root.add(lintelGlow);

  // Window glass panel (dark, emissive stars).
  const windowWidth = frameWidth - 3;
  const windowHeight = frameHeight - 1.2;
  const window = new THREE.Mesh(
    new THREE.PlaneGeometry(windowWidth, windowHeight),
    windowGlow
  );
  window.position.set(
    0,
    windowHeight / 2 + 0.6 + FLOOR_Y,
    HANGAR_CENTER_Z + platformDepth / 2 + 0.12
  );
  root.add(window);

  // Scattered emissive stars on the window to simulate vista.
  const starMat = createGlowMaterial(0xffffff, 1, true);
  const starGeo = new THREE.CircleGeometry(0.08, 8);
  for (let i = 0; i < 90; i++) {
    const x = (Math.random() - 0.5) * (windowWidth - 0.6);
    const y = Math.random() * (windowHeight - 0.6) + 0.9 + FLOOR_Y;
    const star = new THREE.Mesh(starGeo, starMat);
    star.position.set(
      x,
      y,
      HANGAR_CENTER_Z + platformDepth / 2 + 0.15
    );
    star.scale.setScalar(0.35 + Math.random() * 0.65);
    root.add(star);
  }

  // Bigger glowing planet hint in the top corner of the window.
  const planetHint = new THREE.Mesh(
    new THREE.CircleGeometry(1.6, 32),
    createGlowMaterial(0xb4d7ff, 0.75, true)
  );
  planetHint.position.set(
    windowWidth / 2 - 3,
    windowHeight - 3 + FLOOR_Y,
    HANGAR_CENTER_Z + platformDepth / 2 + 0.14
  );
  root.add(planetHint);

  const planetGlow = new THREE.Mesh(
    new THREE.SphereGeometry(2.4, 20, 14),
    createHaloMaterial(0xb4d7ff, 0.12)
  );
  planetGlow.position.copy(planetHint.position);
  root.add(planetGlow);

  // Window mullions (vertical bars inside the window).
  const mullMat = createMatteMaterial({
    color: 0x2a2f42,
    roughness: 0.6
  });
  const mullCount = 5;
  for (let i = 1; i < mullCount; i++) {
    const t = i / mullCount;
    const x = -windowWidth / 2 + t * windowWidth;
    const mull = new THREE.Mesh(
      new THREE.BoxGeometry(0.15, windowHeight, 0.08),
      mullMat
    );
    mull.position.set(
      x,
      windowHeight / 2 + 0.6 + FLOOR_Y,
      HANGAR_CENTER_Z + platformDepth / 2 + 0.14
    );
    root.add(mull);
  }

  // ----- EXIT PAD (between hangar entrance and player ship) ---------
  const exitPos = new THREE.Vector3(0, FLOOR_Y, EXIT_PAD_Z);

  const padBase = new THREE.Mesh(
    new THREE.CylinderGeometry(EXIT_PAD_RADIUS + 0.2, EXIT_PAD_RADIUS + 0.3, 0.25, 48),
    darkMat
  );
  padBase.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + FLOOR_Y + 0.38,
    exitPos.z
  );
  root.add(padBase);

  const padGlow = new THREE.Mesh(
    new THREE.CircleGeometry(EXIT_PAD_RADIUS, 48),
    createMatteMaterial({
      color: 0xffb568,
      roughness: 0.3,
      emissive: 0xffb568,
      emissiveIntensity: 0.55
    })
  );
  padGlow.rotation.x = -Math.PI / 2;
  padGlow.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + FLOOR_Y + 0.51,
    exitPos.z
  );
  root.add(padGlow);

  const padRing = new THREE.Mesh(
    new THREE.TorusGeometry(EXIT_PAD_RADIUS, 0.1, 10, 48),
    warmGlow
  );
  padRing.rotation.x = Math.PI / 2;
  padRing.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + FLOOR_Y + 0.55,
    exitPos.z
  );
  root.add(padRing);

  // "Departure arrow" on the pad pointing toward the window.
  const arrowBody = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.08, 1.6),
    createGlowMaterial(0x3d2a12, 0.95, true)
  );
  arrowBody.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + FLOOR_Y + 0.55,
    exitPos.z - 0.3
  );
  root.add(arrowBody);

  const arrowHead = new THREE.Mesh(
    new THREE.ConeGeometry(0.7, 1.1, 3),
    createGlowMaterial(0x3d2a12, 0.95, true)
  );
  arrowHead.rotation.x = Math.PI / 2;
  arrowHead.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + FLOOR_Y + 0.58,
    exitPos.z + 0.7
  );
  root.add(arrowHead);

  // Beacon columns flanking the exit pad.
  for (const sign of [-1, 1] as const) {
    const column = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22, 0.32, 5.6, 12),
      trimMat
    );
    column.position.set(
      exitPos.x + sign * 3.2,
      HANGAR_PLATFORM_RAISE + 3 + FLOOR_Y,
      exitPos.z
    );
    root.add(column);

    const lamp = new THREE.Mesh(
      new THREE.SphereGeometry(0.42, 14, 10),
      warmGlow
    );
    lamp.position.set(
      exitPos.x + sign * 3.2,
      HANGAR_PLATFORM_RAISE + 5.8 + FLOOR_Y,
      exitPos.z
    );
    root.add(lamp);

    const verticalStripe = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 5.2, 0.14),
      warmAccent
    );
    verticalStripe.position.set(
      exitPos.x + sign * 3.38,
      HANGAR_PLATFORM_RAISE + 3 + FLOOR_Y,
      exitPos.z
    );
    root.add(verticalStripe);
  }

  // Overhead banner arch above the exit.
  const banner = new THREE.Mesh(
    new THREE.BoxGeometry(8, 0.5, 1.5),
    darkMat
  );
  banner.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + 6.5 + FLOOR_Y,
    exitPos.z
  );
  root.add(banner);

  const bannerGlow = new THREE.Mesh(
    new THREE.PlaneGeometry(6.5, 0.6),
    warmGlow
  );
  bannerGlow.rotation.x = Math.PI / 2;
  bannerGlow.position.set(
    exitPos.x,
    HANGAR_PLATFORM_RAISE + 6.26 + FLOOR_Y,
    exitPos.z
  );
  root.add(bannerGlow);

  // ----- PLAYER SHIP DOCK PLATFORM ------------------------------------
  const shipPlatform = new THREE.Mesh(
    new THREE.CylinderGeometry(3.6, 4.0, 0.55, 36),
    trimMat
  );
  shipPlatform.position.set(
    0,
    HANGAR_PLATFORM_RAISE + 0.27 + FLOOR_Y,
    PLAYER_SHIP_DOCK_Z
  );
  root.add(shipPlatform);

  const platformRing = new THREE.Mesh(
    new THREE.TorusGeometry(3.7, 0.08, 10, 56),
    warmGlow
  );
  platformRing.rotation.x = Math.PI / 2;
  platformRing.position.set(
    0,
    HANGAR_PLATFORM_RAISE + 0.55 + FLOOR_Y,
    PLAYER_SHIP_DOCK_Z
  );
  root.add(platformRing);

  // Holographic docking clamps around the ship.
  const clampMat = createGlowMaterial(0xb4e3ff, 0.9, true);
  for (let i = 0; i < 3; i++) {
    const angle = (i / 3) * Math.PI * 2 + Math.PI / 3;
    const clamp = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.9, 0.18),
      clampMat
    );
    const rad = 3.0;
    clamp.position.set(
      Math.cos(angle) * rad,
      HANGAR_PLATFORM_RAISE + 0.95 + FLOOR_Y,
      PLAYER_SHIP_DOCK_Z + Math.sin(angle) * rad
    );
    root.add(clamp);
  }

  // Small player ship platform collision (allow standing close).
  obstacles.push({
    position: new THREE.Vector3(0, 0, PLAYER_SHIP_DOCK_Z),
    radius: 2.0
  });

  // ----- NPC SHIP DOCKS (2, lateral) ----------------------------------
  const npcDockPositions: THREE.Vector3[] = [];
  for (const sign of [-1, 1] as const) {
    const p = new THREE.Vector3(
      sign * NPC_DOCK_OFFSET,
      0,
      HANGAR_CENTER_Z + platformDepth * 0.1
    );
    npcDockPositions.push(p);

    const dock = new THREE.Mesh(
      new THREE.CylinderGeometry(2.7, 3.0, 0.4, 30),
      trimMat
    );
    dock.position.set(p.x, HANGAR_PLATFORM_RAISE + 0.2 + FLOOR_Y, p.z);
    root.add(dock);

    const dockRing = new THREE.Mesh(
      new THREE.TorusGeometry(2.7, 0.05, 8, 36),
      createGlowMaterial(0x6ac0ff, 0.85, true)
    );
    dockRing.rotation.x = Math.PI / 2;
    dockRing.position.set(p.x, HANGAR_PLATFORM_RAISE + 0.42 + FLOOR_Y, p.z);
    root.add(dockRing);

    // Low partition walls between the player dock and the NPC docks.
    const partition = new THREE.Mesh(
      new THREE.BoxGeometry(0.2, 2.5, 5.2),
      createMatteMaterial({
        color: 0x1a2134,
        roughness: 0.7
      })
    );
    partition.position.set(
      sign * (NPC_DOCK_OFFSET - 6),
      HANGAR_PLATFORM_RAISE + 1.25 + FLOOR_Y,
      HANGAR_CENTER_Z
    );
    root.add(partition);

    const partitionAccent = new THREE.Mesh(
      new THREE.BoxGeometry(0.24, 0.1, 5.0),
      createMatteMaterial({
        color: 0x6ac0ff,
        roughness: 0.35,
        emissive: 0x6ac0ff,
        emissiveIntensity: 0.5
      })
    );
    partitionAccent.position.set(
      sign * (NPC_DOCK_OFFSET - 6),
      HANGAR_PLATFORM_RAISE + 2.45 + FLOOR_Y,
      HANGAR_CENTER_Z
    );
    root.add(partitionAccent);

    obstacles.push({ position: new THREE.Vector3(p.x, 0, p.z), radius: 2.4 });
    obstacles.push({
      position: new THREE.Vector3(
        sign * (NPC_DOCK_OFFSET - 6),
        0,
        HANGAR_CENTER_Z
      ),
      radius: 0.6
    });
  }

  return {
    root,
    exitPosition: new THREE.Vector3(
      exitPos.x,
      HANGAR_PLATFORM_RAISE + FLOOR_Y,
      exitPos.z
    ),
    exitRadius: EXIT_PAD_RADIUS,
    playerShipPlatformPosition: new THREE.Vector3(
      0,
      HANGAR_PLATFORM_RAISE + 1.3 + FLOOR_Y,
      PLAYER_SHIP_DOCK_Z
    ),
    npcDockPositions,
    obstacles
  };
}
